import { useActiveOrganization } from '@/features/organization/active-organization-context';

/**
 * Presentation-only capability derivation (Phase G-1, Job 19). The backend
 * never exposes a computed permission list to the frontend - `AuthIdentity`
 * carries only `roleId`/`roleName`/`roleScope` per membership
 * (`server/src/services/auth/types.ts`). This module mirrors the
 * already-ratified role -> permission matrix verbatim from
 * `docs/15_RBAC_PERMISSION_MATRIX.md`, exactly as
 * `server/src/authz/permissions.ts`'s own `ORGANIZATION_ROLE_PERMISSION_LIST`
 * encodes it, so a UI element can hide an action the backend would reject
 * without reinventing authorization logic ad hoc per component.
 *
 * This is NOT the security boundary. The backend re-derives and enforces
 * every permission, and additionally narrows object-level Self/Child access
 * per request (`authz/student-scope.ts`) - a narrowing this module cannot
 * see and does not attempt to replicate. Hiding a button here only avoids
 * showing an action the backend would refuse; every mutation still goes
 * through the real authorization check server-side.
 *
 * Known limitation: a Platform Super Admin's cross-tenant grant
 * (`platformAdminOrganizationPermissionsFor` server-side) comes from
 * `platformMemberships`, not `organizationMemberships`, so a platform-only
 * Super Admin with no organization membership of their own has no
 * `roleName` for this module to key off. This mirrors the existing,
 * already-accepted "zero Active Organization membership" gap from ADR-026's
 * Addendum - out of scope for this phase, not silently patched over.
 */

export type OrganizationCapability =
  | 'organizations:read'
  | 'learning_spaces:read'
  | 'learning_spaces:manage'
  | 'classes:read'
  | 'classes:manage'
  | 'teachers:read'
  | 'teachers:manage'
  | 'students:read'
  | 'students:manage'
  | 'memorization_targets:read'
  | 'memorization_targets:manage'
  | 'memorization_records:read'
  | 'memorization_records:manage'
  | 'murajaah_sessions:read'
  | 'murajaah_sessions:manage'
  | 'evaluations:read'
  | 'evaluations:manage'
  | 'reports:read'
  | 'reports:manage'
  | 'notifications:read'
  | 'notifications:manage'
  | 'settings:read'
  | 'settings:manage'
  | 'messages:read'
  | 'messages:manage';

/** Transcribed from `server/src/authz/permissions.ts`'s own matrix - do not hand-edit independently of it. */
const ORGANIZATION_ROLE_CAPABILITIES: Readonly<Record<string, readonly OrganizationCapability[]>> =
  {
    'Organization Admin': [
      'organizations:read',
      'learning_spaces:read',
      'learning_spaces:manage',
      'classes:read',
      'classes:manage',
      'teachers:read',
      'teachers:manage',
      'students:read',
      'students:manage',
      'memorization_targets:read',
      'memorization_targets:manage',
      'memorization_records:read',
      'memorization_records:manage',
      'murajaah_sessions:read',
      'murajaah_sessions:manage',
      'evaluations:read',
      'evaluations:manage',
      'reports:read',
      'reports:manage',
      'notifications:read',
      'notifications:manage',
      'settings:read',
      'settings:manage',
      'messages:read',
    ],
    Teacher: [
      'learning_spaces:read',
      'classes:read',
      'classes:manage',
      'teachers:read',
      'students:read',
      'students:manage',
      'memorization_targets:read',
      'memorization_targets:manage',
      'memorization_records:read',
      'memorization_records:manage',
      'murajaah_sessions:read',
      'murajaah_sessions:manage',
      'evaluations:read',
      'evaluations:manage',
      'reports:read',
      'reports:manage',
      'notifications:read',
      'notifications:manage',
      'messages:read',
      'messages:manage',
    ],
    Student: [
      'learning_spaces:read',
      'classes:read',
      'students:read',
      'memorization_targets:read',
      'memorization_records:read',
      'memorization_records:manage',
      'murajaah_sessions:read',
      'evaluations:read',
      'reports:read',
      'notifications:read',
      'messages:read',
      'messages:manage',
    ],
    Parent: [
      'learning_spaces:read',
      'classes:read',
      'students:read',
      'memorization_targets:read',
      'memorization_records:read',
      'murajaah_sessions:read',
      'evaluations:read',
      'reports:read',
      'notifications:read',
    ],
  };

export function capabilitiesForRole(roleName: string): ReadonlySet<OrganizationCapability> {
  const list = ORGANIZATION_ROLE_CAPABILITIES[roleName];
  return list === undefined ? EMPTY_SET : new Set(list);
}

const EMPTY_SET: ReadonlySet<OrganizationCapability> = new Set();

/**
 * Whether the current Active Organization membership's role carries
 * `capability`. `false` for every state where that cannot be determined
 * (no active organization, unknown role) - default deny, matching the
 * backend's own fail-secure posture.
 */
export function useHasCapability(capability: OrganizationCapability): boolean {
  const { activeMembership } = useActiveOrganization();
  if (activeMembership === null) {
    return false;
  }
  return capabilitiesForRole(activeMembership.roleName).has(capability);
}
